import { audio, chats as chatStore, outlines, prose } from '../storage/db';
import { useAppStore } from '../stores/useAppStore';
import type { ChatMessage, Settings } from '../types';
import {
  SyncError,
  headBlob,
  putBlob,
  putObject,
  syncMe,
} from './syncClient';

export type RescuePhase =
  | 'auth'
  | 'settings'
  | 'universes'
  | 'slugs'
  | 'stories'
  | 'chats'
  | 'chat_messages'
  | 'outlines'
  | 'prose'
  | 'audio';

export type RescueProgress = {
  phase: RescuePhase;
  done: number;
  total: number;
  message?: string;
};

export type RescueOptions = {
  onProgress?: (p: RescueProgress) => void;
  onLog?: (line: string) => void;
};

export type RescueSummary = {
  phases: Record<RescuePhase, { uploaded: number; skipped: number; failed: number }>;
};

function newSummary(): RescueSummary {
  const phases = {} as RescueSummary['phases'];
  const all: RescuePhase[] = [
    'auth', 'settings', 'universes', 'slugs', 'stories',
    'chats', 'chat_messages', 'outlines', 'prose', 'audio',
  ];
  for (const p of all) phases[p] = { uploaded: 0, skipped: 0, failed: 0 };
  return { phases };
}

export async function rescueToServer(opts: RescueOptions = {}): Promise<RescueSummary> {
  const onProgress = opts.onProgress ?? (() => {});
  const onLog = opts.onLog ?? (() => {});
  const summary = newSummary();

  onProgress({ phase: 'auth', done: 0, total: 1 });
  const me = await syncMe();
  onLog(`Authed as ${me.user}.`);
  summary.phases.auth.uploaded = 1;
  onProgress({ phase: 'auth', done: 1, total: 1 });

  const state = useAppStore.getState();

  onProgress({ phase: 'settings', done: 0, total: 1 });
  await putObject('settings', 'global', stripSecrets(state.settings));
  summary.phases.settings.uploaded = 1;
  onProgress({ phase: 'settings', done: 1, total: 1 });
  onLog('Settings uploaded (without API keys).');

  await uploadObjectMap(
    'universe',
    'universes',
    state.universes,
    summary,
    onProgress,
    onLog
  );

  await uploadObjectArray(
    'slug',
    'slugs',
    state.slugPool,
    (s) => s.id,
    summary,
    onProgress,
    onLog
  );

  await uploadObjectMap(
    'story',
    'stories',
    state.stories,
    summary,
    onProgress,
    onLog
  );

  await uploadObjectMap(
    'chat',
    'chats',
    state.chats,
    summary,
    onProgress,
    onLog
  );

  await uploadIdbObjects<ChatMessage[]>(
    'chat_messages',
    chatStore,
    summary,
    onProgress,
    onLog
  );

  await uploadIdbText('outlines', 'outline_', outlines, summary, onProgress, onLog);
  await uploadIdbText('prose', 'prose_', prose, summary, onProgress, onLog);
  await uploadIdbAudio(summary, onProgress, onLog);

  return summary;
}

async function uploadObjectMap<T extends { id: string }>(
  kind: string,
  phase: RescuePhase,
  map: Record<string, T>,
  summary: RescueSummary,
  onProgress: (p: RescueProgress) => void,
  onLog: (s: string) => void
) {
  const items = Object.values(map);
  onProgress({ phase, done: 0, total: items.length });
  for (let i = 0; i < items.length; i++) {
    try {
      await putObject(kind, items[i].id, items[i]);
      summary.phases[phase].uploaded++;
    } catch (e) {
      summary.phases[phase].failed++;
      onLog(`${kind} ${items[i].id}: ${(e as Error).message}`);
    }
    onProgress({ phase, done: i + 1, total: items.length });
  }
  onLog(
    `${phase}: ${summary.phases[phase].uploaded} uploaded` +
      (summary.phases[phase].failed
        ? `, ${summary.phases[phase].failed} failed`
        : '')
  );
}

async function uploadObjectArray<T>(
  kind: string,
  phase: RescuePhase,
  items: T[],
  idOf: (item: T) => string,
  summary: RescueSummary,
  onProgress: (p: RescueProgress) => void,
  onLog: (s: string) => void
) {
  onProgress({ phase, done: 0, total: items.length });
  for (let i = 0; i < items.length; i++) {
    try {
      await putObject(kind, idOf(items[i]), items[i]);
      summary.phases[phase].uploaded++;
    } catch (e) {
      summary.phases[phase].failed++;
      onLog(`${kind} ${idOf(items[i])}: ${(e as Error).message}`);
    }
    onProgress({ phase, done: i + 1, total: items.length });
  }
  onLog(
    `${phase}: ${summary.phases[phase].uploaded} uploaded` +
      (summary.phases[phase].failed
        ? `, ${summary.phases[phase].failed} failed`
        : '')
  );
}

type IdbObjectStore<T> = {
  keys(): Promise<IDBValidKey[]>;
  get(key: string): Promise<T | undefined>;
};

async function uploadIdbObjects<T>(
  phase: RescuePhase,
  store: IdbObjectStore<T>,
  summary: RescueSummary,
  onProgress: (p: RescueProgress) => void,
  onLog: (s: string) => void
) {
  const keys = (await store.keys()).map(String);
  onProgress({ phase, done: 0, total: keys.length });
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    try {
      const value = await store.get(k);
      if (value !== undefined) {
        await putObject(phase, k, value);
        summary.phases[phase].uploaded++;
      }
    } catch (e) {
      summary.phases[phase].failed++;
      if (e instanceof SyncError && e.kind === 'too_large') {
        onLog(`${phase} ${k}: too large for object store, skipped.`);
      } else {
        onLog(`${phase} ${k}: ${(e as Error).message}`);
      }
    }
    onProgress({ phase, done: i + 1, total: keys.length });
  }
  onLog(
    `${phase}: ${summary.phases[phase].uploaded} uploaded` +
      (summary.phases[phase].failed
        ? `, ${summary.phases[phase].failed} failed`
        : '')
  );
}

async function uploadIdbText(
  phase: 'outlines' | 'prose',
  prefix: string,
  store: IdbObjectStore<string>,
  summary: RescueSummary,
  onProgress: (p: RescueProgress) => void,
  onLog: (s: string) => void
) {
  const keys = (await store.keys()).map(String);
  onProgress({ phase, done: 0, total: keys.length });
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    try {
      const text = await store.get(k);
      if (text !== undefined) {
        const blobKey = `${prefix}${k}`;
        const expectedSize = new TextEncoder().encode(text).length;
        const head = await headBlob(blobKey);
        if (head && head.size === expectedSize) {
          summary.phases[phase].skipped++;
        } else {
          await putBlob(blobKey, text, 'text/plain; charset=utf-8');
          summary.phases[phase].uploaded++;
        }
      }
    } catch (e) {
      summary.phases[phase].failed++;
      onLog(`${phase} ${k}: ${(e as Error).message}`);
    }
    onProgress({
      phase,
      done: i + 1,
      total: keys.length,
      message: `${summary.phases[phase].uploaded}↑ ${summary.phases[phase].skipped}=`,
    });
  }
  const s = summary.phases[phase];
  onLog(`${phase}: ${s.uploaded} uploaded, ${s.skipped} already on server${s.failed ? `, ${s.failed} failed` : ''}.`);
}

async function uploadIdbAudio(
  summary: RescueSummary,
  onProgress: (p: RescueProgress) => void,
  onLog: (s: string) => void
) {
  const phase: RescuePhase = 'audio';
  const keys = (await audio.keys()).map(String);
  onProgress({ phase, done: 0, total: keys.length });
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    try {
      const blob = await audio.get(k);
      if (blob) {
        const blobKey = `audio_${k}`;
        const head = await headBlob(blobKey);
        if (head && head.size === blob.size) {
          summary.phases.audio.skipped++;
        } else {
          await putBlob(blobKey, blob, blob.type || 'audio/mpeg');
          summary.phases.audio.uploaded++;
        }
      }
    } catch (e) {
      summary.phases.audio.failed++;
      onLog(`audio ${k}: ${(e as Error).message}`);
    }
    onProgress({
      phase,
      done: i + 1,
      total: keys.length,
      message: `${summary.phases.audio.uploaded}↑ ${summary.phases.audio.skipped}=`,
    });
  }
  const s = summary.phases.audio;
  onLog(`audio: ${s.uploaded} uploaded, ${s.skipped} already on server${s.failed ? `, ${s.failed} failed` : ''}.`);
}

function stripSecrets(s: Settings): Settings {
  return {
    ...s,
    apiBaseUrl: undefined,
    apiToken: undefined,
  };
}
